import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuoteDispatch } from '../../../stateProviders/QuoteStateProvider';
import { ReactNode, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../../globalComponents/LoadingSpinner';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Typography,
} from '@mui/material';
import { useLocalStorageSync } from '../../../hooks/useLocalStorageSync';
import { toastInfo } from '../../../Toaster/Toast';

//This component wraps the components that depend on the current quote
export const QuoteFetchWrapper: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { quoteId } = useParams();
	const navigate = useNavigate();
	const quoteDispatch = useQuoteDispatch();
	const api = useApi();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const { getLocalDraft, reconcile, clearLocal } = useLocalStorageSync(quoteId);

	useEffect(() => {
		if (!quoteId) return;
		(async () => {
			try {
				const quote = await api.quotations.getQuotationById(quoteId);

				if (quote.errored) {
					if (
						quote.error.message.includes('permission') ||
						quote.error.message.includes('found')
					) {
						setError(quote.error.message);
					}
					setLoading(false);
					return;
				}

				const localDraft = getLocalDraft();

				if (localDraft) {
					const decision = reconcile(quote);

					switch (decision) {
						case 'use_local':
							quoteDispatch({
								...quote,
								blocks: localDraft.blocks,
								...localDraft.quoteState,
								currentQuote: quote,
							});
							break;

						case 'use_server':
							clearLocal();
							quoteDispatch({ ...quote, currentQuote: quote });
							break;

						case 'conflict':
							clearLocal();
							quoteDispatch({ ...quote, currentQuote: quote });
							toastInfo({
								title: 'Conflict Detected',
								description:
									'Server has newer changes. Local changes have been discarded.',
							});
							break;
					}
				} else {
					quoteDispatch({ ...quote, currentQuote: quote });
				}
			} catch (err) {
				setError('Failed to load quote. Please try again.');
			}

			setLoading(false);
		})();
	}, [quoteId]);

	if (loading) {
		return (
			<div className="h-full flex items-center justify-center">
				<LoadingSpinner />
			</div>
		);
	}

	if (error) {
		return (
			<Dialog
				open={error}
				onClose={() => {
					navigate('/quotations');
				}}
			>
				<DialogContent sx={{ backgroundColor: 'white' }}>
					<Typography sx={{ color: 'red' }}>{error}</Typography>
				</DialogContent>

				<DialogActions sx={{ backgroundColor: 'white' }}>
					<Button
						sx={{
							backgroundColor: 'primary.main',
							color: 'white',
							':hover': { backgroundColor: 'primary.main', color: 'white' },
						}}
						onClick={() => {
							navigate('/quotations');
						}}
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	return children;
};
